import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  Divider,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save, PlaylistAdd } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, ServiceEntryDetail } from '../../services/procurementService';

interface ServicePoOption {
  id: number;
  po_number: string;
  vendor_id: string;
  vendor_name: string;
  status: string;
}

interface PoLineSuggestion {
  description: string;
  quantity: number | string;
  uom: string;
  rate: number | string;
}

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

let draftKeySeq = 0;
const nextDraftKey = () => `sesline-${++draftKeySeq}`;

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  uom: string;
  rate: string;
}

const emptyLine = (): LineDraft => ({
  key: nextDraftKey(),
  description: '',
  quantity: '1',
  uom: '',
  rate: '',
});

const ServiceEntryFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const prefillPoId = searchParams.get('poId');
  const prefillSwoId = searchParams.get('swoId');
  const isEdit = !!id;

  const [pos, setPos] = useState<ServicePoOption[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(isEdit);

  const [selectedPo, setSelectedPo] = useState<ServicePoOption | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [poLines, setPoLines] = useState<PoLineSuggestion[]>([]);
  const [loadingPoLines, setLoadingPoLines] = useState(false);

  const [servicePeriodFrom, setServicePeriodFrom] = useState(todayISO());
  const [servicePeriodTo, setServicePeriodTo] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    api
      .get<{ purchaseOrders: ServicePoOption[] }>('/procurement/purchase-orders', { params: { poType: 'SERVICE' } })
      .then((r) => setPos(r.purchaseOrders || []))
      .catch(() => setPos([]))
      .finally(() => setLoadingRefData(false));
  }, []);

  const loadPoDetail = useCallback((po: ServicePoOption | null) => {
    setPoLines([]);
    if (!po) return;
    setLoadingPoLines(true);
    api
      .get<{ purchaseOrder: any }>(`/procurement/purchase-orders/${po.id}`)
      .then(({ purchaseOrder }) => {
        setVendorId(purchaseOrder.vendor_id);
        setVendorName(purchaseOrder.vendor_name || po.vendor_name);
        setPoLines(
          (purchaseOrder.items || []).map((it: any) => ({
            description: it.product_name || it.line_description || 'Service',
            quantity: it.quantity,
            uom: it.line_uom || it.unit || '',
            rate: it.unit_price,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingPoLines(false));
  }, []);

  const handleSelectPo = (po: ServicePoOption | null) => {
    setSelectedPo(po);
    setVendorId(po?.vendor_id || '');
    setVendorName(po?.vendor_name || '');
    loadPoDetail(po);
  };

  // Prefill from ?poId= once PO options are loaded (new-entry mode only).
  useEffect(() => {
    if (isEdit || loadingRefData || !prefillPoId || selectedPo) return;
    const match = pos.find((p) => String(p.id) === String(prefillPoId));
    if (match) handleSelectPo(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, loadingRefData, prefillPoId, pos]);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingEntry(true);
    procurementService
      .getServiceEntry(id)
      .then(({ serviceEntry }: { serviceEntry: ServiceEntryDetail }) => {
        setSelectedPo({
          id: serviceEntry.po_id,
          po_number: serviceEntry.po_number,
          vendor_id: serviceEntry.vendor_id,
          vendor_name: serviceEntry.vendor_name,
          status: '',
        });
        setVendorId(serviceEntry.vendor_id);
        setVendorName(serviceEntry.vendor_name);
        setServicePeriodFrom(serviceEntry.service_period_from ? serviceEntry.service_period_from.slice(0, 10) : todayISO());
        setServicePeriodTo(serviceEntry.service_period_to ? serviceEntry.service_period_to.slice(0, 10) : todayISO());
        setDescription(serviceEntry.description || '');
        setRemarks(serviceEntry.remarks || '');
        setStatus(serviceEntry.status);
        setLines(
          (serviceEntry.lines || []).map((l) => ({
            key: nextDraftKey(),
            description: l.description || '',
            quantity: String(l.quantity ?? ''),
            uom: l.uom || '',
            rate: String(l.rate ?? ''),
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load service entry sheet'))
      .finally(() => setLoadingEntry(false));
  }, [isEdit, id]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addFromPoLine = (suggestion: PoLineSuggestion) => {
    setLines((prev) => [
      ...prev,
      {
        key: nextDraftKey(),
        description: suggestion.description,
        quantity: String(toNum(suggestion.quantity) || 1),
        uom: suggestion.uom,
        rate: String(toNum(suggestion.rate)),
      },
    ]);
  };

  const total = useMemo(() => lines.reduce((sum, l) => sum + toNum(l.quantity) * toNum(l.rate), 0), [lines]);

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!selectedPo) errs.push('A service purchase order is required.');
    if (!servicePeriodFrom || !servicePeriodTo) errs.push('Service period from/to is required.');
    if (lines.length === 0) errs.push('At least one service line is required.');
    lines.forEach((l, idx) => {
      if (!l.description.trim()) errs.push(`Line ${idx + 1}: description is required.`);
      if (toNum(l.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
      if (toNum(l.rate) < 0) errs.push(`Line ${idx + 1}: rate cannot be negative.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0 || !selectedPo) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        poId: selectedPo.id,
        serviceWorkOrderId: prefillSwoId ? Number(prefillSwoId) : undefined,
        vendorId,
        servicePeriodFrom,
        servicePeriodTo,
        description: description.trim() || undefined,
        remarks: remarks.trim() || undefined,
        lines: lines.map((l) => ({
          description: l.description.trim(),
          quantity: toNum(l.quantity),
          uom: l.uom.trim() || undefined,
          rate: toNum(l.rate),
        })),
      };

      const { serviceEntry } =
        isEdit && id
          ? await procurementService.updateServiceEntry(id, payload)
          : await procurementService.createServiceEntry(payload);

      navigate(`/procurement/service-entries/${serviceEntry.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save service entry sheet');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingEntry;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(isEdit && id ? `/procurement/service-entries/${id}` : '/procurement/service-entries')}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Service Entry Sheet' : 'New Service Entry Sheet'}
        subtitle="Record the services actually delivered against a service purchase order, before it can be invoiced"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This service entry sheet is {status} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Service Entry Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              options={pos}
              size="small"
              value={selectedPo}
              onChange={(_, v) => handleSelectPo(v)}
              getOptionLabel={(o) => `${o.po_number} — ${o.vendor_name}`}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Service Purchase Order *" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth size="small" label="Vendor" value={vendorName} disabled InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Service Period From *"
              value={servicePeriodFrom}
              onChange={(e) => setServicePeriodFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Service Period To *"
              value={servicePeriodTo}
              onChange={(e) => setServicePeriodTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedPo && poLines.length > 0 && canEdit && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            PO Lines (for reference — add as service lines)
          </Typography>
          {loadingPoLines ? (
            <CircularProgress size={20} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {poLines.map((pl, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.5,
                    borderBottom: idx < poLines.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {pl.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {toNum(pl.quantity)} {pl.uom} @ {formatIndianNumber(toNum(pl.rate))}
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<PlaylistAdd />} onClick={() => addFromPoLine(pl)}>
                    Add
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Service Lines
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addLine}>
              Add Line
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Description</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty / Hours</TableCell>
                <TableCell sx={headerCellSx}>UOM</TableCell>
                <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={l.description}
                      onChange={(e) => updateLine(l.key, { description: e.target.value })}
                      disabled={!canEdit}
                      placeholder="Service description"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={l.uom}
                      onChange={(e) => updateLine(l.key, { uom: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 110 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatIndianNumber(toNum(l.quantity) * toNum(l.rate))}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Total: {formatIndianNumber(total)}
          </Typography>
        </Box>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={() =>
              navigate(isEdit && id ? `/procurement/service-entries/${id}` : '/procurement/service-entries')
            }
          >
            Cancel
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Service Entry'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ServiceEntryFormPage;
